import { useEffect, useRef } from 'react'
import { RouterProvider } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { HelmetProvider } from 'react-helmet-async'
import { router } from './app/router'
import { queryClient } from './shared/api/queryClient'
import { BottomSheetHost, ModalHost, ToastHost } from './shared/ui'
import {
  clearChunkReloadAttemptSoon,
  clearChunkReloadParam,
  recoverFromChunkLoadError,
} from './shared/utils/chunkReload'
import { useAppVersionGuard } from './shared/utils/appVersion'

function ChromeDragGuard() {
  useEffect(() => {
    const preventChromeDrag = (event: TouchEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (!target.closest('[data-qsc-chrome="true"]')) return
      if (event.cancelable) event.preventDefault()
    }

    document.addEventListener('touchmove', preventChromeDrag, { passive: false })
    return () => document.removeEventListener('touchmove', preventChromeDrag)
  }, [])

  return null
}

function isVerticalScrollable(element: HTMLElement) {
  const style = window.getComputedStyle(element)
  if (!/(auto|scroll|overlay)/.test(style.overflowY)) return false
  return element.scrollHeight - element.clientHeight > 1
}

function findScrollBoundaryTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return null

  const rootScroll = target.closest('[data-qsc-scroll="content"]')
  if (!(rootScroll instanceof HTMLElement)) return null

  let element: Element | null = target
  while (element && element !== document.body) {
    if (element instanceof HTMLElement && isVerticalScrollable(element)) return element
    if (element === rootScroll) break
    element = element.parentElement
  }

  return rootScroll
}

function ScrollBoundaryGuard() {
  // 스크롤 대상은 touchstart 에서 한 번만 찾는다. touchmove 마다 getComputedStyle 로
  // DOM 을 거슬러 올라가면 iOS WebView 에서 스크롤이 뻑뻑해진다.
  const touchRef = useRef<{
    startX: number
    startY: number
    scrollTarget: HTMLElement | null
  } | null>(null)

  useEffect(() => {
    const rememberTouch = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!touch) {
        touchRef.current = null
        return
      }
      const target = event.target
      const inChrome = target instanceof Element && !!target.closest('[data-qsc-chrome="true"]')
      touchRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        scrollTarget: inChrome ? null : findScrollBoundaryTarget(target),
      }
    }

    const preventScrollChaining = (event: TouchEvent) => {
      const touch = event.touches[0]
      const previous = touchRef.current
      if (!touch || !previous) return

      const { scrollTarget } = previous
      if (!scrollTarget) return

      const totalDeltaX = touch.clientX - previous.startX
      const totalDeltaY = touch.clientY - previous.startY

      if (Math.abs(totalDeltaY) < 4) return
      if (Math.abs(totalDeltaY) <= Math.abs(totalDeltaX)) return

      // 스크롤할 내용이 없는 영역만 막는다. 목록 끝에서의 체이닝은 CSS overscroll-behavior 가
      // 막는다. 여기서 끝 지점 터치를 preventDefault 하면 iOS 는 그 제스처 전체를 멈춰서
      // 짧은 목록일수록 스크롤이 걸리는 느낌이 난다.
      const maxScrollTop = scrollTarget.scrollHeight - scrollTarget.clientHeight
      if (maxScrollTop <= 1 && event.cancelable) event.preventDefault()
    }

    const clearTouch = () => {
      touchRef.current = null
    }

    document.addEventListener('touchstart', rememberTouch, { passive: true })
    document.addEventListener('touchmove', preventScrollChaining, { passive: false })
    document.addEventListener('touchend', clearTouch, { passive: true })
    document.addEventListener('touchcancel', clearTouch, { passive: true })

    return () => {
      document.removeEventListener('touchstart', rememberTouch)
      document.removeEventListener('touchmove', preventScrollChaining)
      document.removeEventListener('touchend', clearTouch)
      document.removeEventListener('touchcancel', clearTouch)
    }
  }, [])

  return null
}

function ChunkReloadGuard() {
  useEffect(() => {
    clearChunkReloadParam()
    clearChunkReloadAttemptSoon()

    const handlePreloadError = (event: Event) => {
      event.preventDefault()
      recoverFromChunkLoadError({ type: 'vite:preloadError' })
    }

    const handleError = (event: ErrorEvent) => {
      if (recoverFromChunkLoadError(event.error ?? event.message)) {
        event.preventDefault()
      }
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (recoverFromChunkLoadError(event.reason)) {
        event.preventDefault()
      }
    }

    window.addEventListener('vite:preloadError', handlePreloadError)
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('vite:preloadError', handlePreloadError)
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return null
}

export default function App() {
  useAppVersionGuard()

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ChromeDragGuard />
        <ScrollBoundaryGuard />
        <ChunkReloadGuard />
        <RouterProvider router={router} />
        <ModalHost />
        <BottomSheetHost />
        <ToastHost />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
      </QueryClientProvider>
    </HelmetProvider>
  )
}
